import React, { useCallback, useEffect, useRef, useState } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { ILease1099, ILeaseMaster } from 'src/models/dlcLease/ILeaseMaster';
import { ILeaseInvoice } from 'src/models/dlcLease/ILeaseInvoice';
import { ILeaseRecording } from 'src/models/dlcLease/ILeaseRecording';
import { RouteComponentProps } from 'react-router-dom';

export type FieldValue = string | number | boolean | Date | null | undefined | Record<string, unknown> | unknown[];
type NestedObject = { [key: string]: FieldValue | NestedObject };

import LeaseHeaderSection from './sections/Header';
import MasterSection from './sections/Master';
import InvoiceSection from './sections/Invoice';
import LessorSection from './sections/Lessor';
import DescriptionSection from './sections/Description';
import TractSection from './sections/Tract';
import Section1099 from './sections/1099';
import { LeaseNameAddress } from './sections/tinNameAddressMatching';
import NotesSection from './sections/Notes';
import ClientAddressSection from './sections/ClientAddress';
import TransactionsSection from './sections/Transactions';
import IssuesSection from './sections/Issues';
import LeaseHeaderControls from './sections/LeaseHeaderControls';
import * as styles from './sections/styles';
import { clearLeaseRecordsFieldError, ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from './LeaseRecordsApiError';

interface ILeasePageParams {
    id: string;
}

const nullableInvoiceDateFields: (keyof ILeaseInvoice)[] = [
    'invDate',
    'dueDate1',
    'dueDate2',
    'chkDate1',
    'chkDate2',
    'ihDate',
    'leaseIhDate',
    'arDate',
    'checkSentDate',
    'poolingDate',
    'collectionDate',
    'voidDate',
    'dateFunded',
];

const nullable1099DateFields: (keyof ILease1099)[] = ['chkDate', 'correctDate', 'transmitDate', 'correctTransDate', 'w9VerifyDate'];
const nullableRecordingDateFields: (keyof ILeaseRecording)[] = ['recDate', 'assignDate', 'tranDate', 'modDt'];
const nullableMasterDateFields: (keyof ILeaseMaster)[] = ['leaseDate', 'effDate', 'expDate', 'exposedDate'];

const normalizeBlankNullableFields = <T extends object>(value: T, fields: (keyof T)[]): Record<string, unknown> => {
    const normalized = Object.assign({}, value) as Record<string, unknown>;
    fields.forEach((field) => {
        const fieldValue = value[field];
        if (typeof fieldValue === 'string' && fieldValue.trim() === '') {
            normalized[field as string] = null;
        }
    });
    return normalized;
};

const normalizeRecordingForSave = (recording: ILeaseRecording): Record<string, unknown> =>
    normalizeBlankNullableFields(recording, nullableRecordingDateFields);

export const normalizeInvoiceForSave = (invoice: ILeaseInvoice): Record<string, unknown> => {
    const normalizedInvoice = normalizeBlankNullableFields(invoice, nullableInvoiceDateFields);
    if (invoice.recordings) {
        normalizedInvoice.recordings = invoice.recordings.map(normalizeRecordingForSave);
    }
    return normalizedInvoice;
};

export const normalize1099ForSave = (item: ILease1099): Record<string, unknown> => normalizeBlankNullableFields(item, nullable1099DateFields);

export const normalizeMasterForSave = (master: Partial<ILeaseMaster>): Record<string, unknown> => {
    const normalizedMaster = normalizeBlankNullableFields(master, nullableMasterDateFields);
    if (master.rentalInfo) {
        normalizedMaster.rentalInfo = normalizeBlankNullableFields(master.rentalInfo, ['dueDate']);
    }
    return normalizedMaster;
};

const LoadingBar: React.FC<{ width?: string; height?: string }> = ({ width = '100%', height = '0.75rem' }) => (
    <span
        aria-hidden={true}
        style={{
            display: 'block',
            width,
            height,
            maxWidth: '100%',
            borderRadius: '4px',
            backgroundColor: '#E5E7EB',
        }}
    />
);

const LoadingField: React.FC<{ width?: string }> = ({ width = '72%' }) => (
    <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
        <LoadingBar width={width} height="0.55rem" />
        <LoadingBar height="2.15rem" />
    </div>
);

const LeasePageLoading: React.FC = () => (
    <div
        aria-busy={true}
        aria-live="polite"
        style={{
            minHeight: '100vh',
            backgroundColor: styles.colors.bgPage,
            padding: '0.75rem 1.5rem 2rem',
            color: styles.colors.gray900,
        }}
    >
        <div style={{ width: '100%', maxWidth: '1700px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div
                style={{
                    minHeight: '2.6rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    padding: '0.45rem 0.75rem',
                    border: `1px solid ${styles.colors.gray200}`,
                    borderRadius: '6px',
                    backgroundColor: styles.colors.white,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flex: '1 1 24rem' }}>
                    <LoadingBar width="7.5rem" height="1.9rem" />
                    <LoadingBar width="5rem" height="1.9rem" />
                    <LoadingBar width="5.75rem" height="1.9rem" />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.65rem', flex: '1 1 28rem' }}>
                    <LoadingBar width="11rem" height="1.9rem" />
                    <LoadingBar width="6rem" height="1.9rem" />
                    <LoadingBar width="8rem" height="1.9rem" />
                </div>
            </div>

            <section
                style={{
                    padding: '1rem',
                    border: `1px solid ${styles.colors.gray200}`,
                    borderRadius: '6px',
                    backgroundColor: styles.colors.white,
                    boxShadow: '0 1px 2px rgba(17, 24, 39, 0.04)',
                }}
            >
                <div
                    style={{
                        minHeight: '2rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '1rem',
                        marginBottom: '1rem',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                        <div
                            style={{
                                width: '1.7rem',
                                height: '1.7rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '4px',
                                backgroundColor: '#E8EEFF',
                                color: styles.colors.primary,
                            }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                            </svg>
                        </div>
                        <span style={{ fontSize: '1rem', fontWeight: 600 }}>Loading lease record</span>
                    </div>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={styles.colors.primary} strokeWidth="2">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                        <animateTransform
                            attributeName="transform"
                            type="rotate"
                            from="0 12 12"
                            to="360 12 12"
                            dur="0.85s"
                            repeatCount="indefinite"
                        />
                    </svg>
                </div>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))',
                        gap: '0.85rem',
                    }}
                >
                    {['55%', '68%', '48%', '62%', '42%', '58%', '50%', '64%', '46%', '60%', '54%', '44%'].map((width, index) => (
                        <LoadingField key={index} width={width} />
                    ))}
                </div>
            </section>

            <div
                style={{
                    minHeight: '2.8rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '1.15rem',
                    overflow: 'hidden',
                    padding: '0.5rem 0.75rem',
                    borderBottom: `1px solid ${styles.colors.gray200}`,
                }}
            >
                {['4.2rem', '4.8rem', '3.8rem', '5.4rem', '4.2rem', '3rem', '3.7rem', '5rem'].map((width, index) => (
                    <LoadingBar key={index} width={width} height="0.8rem" />
                ))}
            </div>

            <section
                style={{
                    padding: '1rem',
                    border: `1px solid ${styles.colors.gray200}`,
                    borderRadius: '6px',
                    backgroundColor: styles.colors.white,
                    boxShadow: '0 1px 2px rgba(17, 24, 39, 0.04)',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', marginBottom: '1rem' }}>
                    <LoadingBar width="1.7rem" height="1.7rem" />
                    <LoadingBar width="9rem" height="1rem" />
                </div>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))',
                        gap: '1rem',
                    }}
                >
                    {['45%', '52%', '38%', '58%', '42%', '48%', '55%', '40%'].map((width, index) => (
                        <LoadingField key={index} width={width} />
                    ))}
                </div>
            </section>
        </div>
    </div>
);

const LeasePage: React.FC<RouteComponentProps<ILeasePageParams>> = ({ match, history }) => {
    const leaseId = match.params.id;
    const { get, post, put, response } = useFetch();
    const [leaseMaster, setLeaseMaster] = useState<ILeaseMaster | null>(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [editedLeaseMaster, setEditedLeaseMaster] = useState<ILeaseMaster | null>(null);
    const [isDirty, setIsDirty] = useState(false);
    const [modifiedSections, setModifiedSections] = useState<Set<string>>(new Set());
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [saveError, setSaveError] = useState<ILeaseRecordsApiError | null>(null);
    const [activeSection, setActiveSection] = useState('master');
    const contentRef = useRef<HTMLDivElement>(null);
    const editedLeaseMasterRef = useRef<ILeaseMaster | null>(null);
    const modifiedSectionsRef = useRef<Set<string>>(new Set());
    const sectionVersionsRef = useRef<Record<string, number>>({});
    const saveCountRef = useRef(0);
    const savingSectionsRef = useRef<Set<string>>(new Set());
    const activeLeaseIdRef = useRef(leaseId);
    const loadRequestIdRef = useRef(0);

    activeLeaseIdRef.current = leaseId;

    const masterRef = useRef<HTMLDivElement>(null);
    const invoiceRef = useRef<HTMLDivElement>(null);
    const lessorRef = useRef<HTMLDivElement>(null);
    const descriptionRef = useRef<HTMLDivElement>(null);
    const tractsRef = useRef<HTMLDivElement>(null);
    const k99Ref = useRef<HTMLDivElement>(null);
    const notesRef = useRef<HTMLDivElement>(null);
    const clientAddressRef = useRef<HTMLDivElement>(null);
    const transactionsRef = useRef<HTMLDivElement>(null);
    const issuesRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (leaseId) {
            fetchLeaseMaster(leaseId);
        }
        // fetchLeaseMaster uses the current use-http request context; leaseId is the
        // lifecycle boundary for loading a different record.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leaseId]);

    // Warn user if they try to navigate away with unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    const fetchLeaseMaster = async (id: string): Promise<void> => {
        const requestId = ++loadRequestIdRef.current;
        setIsInitialLoading(true);
        setLoadError(false);
        try {
            const data = await get(`/v1.0/leases/master/${id}`);
            const isCurrentRequest = requestId === loadRequestIdRef.current && activeLeaseIdRef.current === id;
            if (isCurrentRequest && String(data?.lsNo) === id) {
                // Initialize missing data structures
                if (!data.lessorDetails) {
                    data.lessorDetails = {};
                }
                if (!data.clientAddress) {
                    data.clientAddress = {};
                }

                const editableData = JSON.parse(JSON.stringify(data));
                setLeaseMaster(data);
                setEditedLeaseMaster(editableData);
                editedLeaseMasterRef.current = editableData;
                setIsDirty(false);
                setModifiedSections(new Set());
                modifiedSectionsRef.current = new Set();
                sectionVersionsRef.current = {};
            } else if (isCurrentRequest) {
                setLoadError(true);
            }
        } catch {
            if (requestId === loadRequestIdRef.current && activeLeaseIdRef.current === id) {
                setLoadError(true);
            }
        } finally {
            if (requestId === loadRequestIdRef.current && activeLeaseIdRef.current === id) {
                setIsInitialLoading(false);
            }
        }
    };

    const handleNavigate = (newLeaseId: number) => {
        if (isDirty) {
            const confirmed = window.confirm('You have unsaved changes. Are you sure you want to navigate away and discard them?');
            if (!confirmed) {
                return; // User canceled navigation
            }
        }
        // Use the history object from react-router to change the URL
        history.push(`/leases/${newLeaseId}`);
    };

    const handleProcessingNavigate = (path: string) => {
        if (isDirty) {
            const confirmed = window.confirm('You have unsaved changes. Are you sure you want to navigate away and discard them?');
            if (!confirmed) {
                return;
            }
        }
        history.push(path);
    };

    const handleFieldChange = (field: string, value: FieldValue) => {
        const currentLeaseMaster = editedLeaseMasterRef.current;
        if (!currentLeaseMaster) {
            return;
        }

        const keys = field.split('.');
        const updatedLeaseMaster = { ...currentLeaseMaster };
        let target = updatedLeaseMaster as unknown as NestedObject;

        for (let i = 0; i < keys.length - 1; i++) {
            if (typeof target[keys[i]] !== 'object' || target[keys[i]] === null) {
                target[keys[i]] = {};
            }
            target = target[keys[i]] as NestedObject;
        }

        target[keys[keys.length - 1]] = value;

        const nextLeaseMaster = updatedLeaseMaster as ILeaseMaster;
        editedLeaseMasterRef.current = nextLeaseMaster;
        setEditedLeaseMaster(nextLeaseMaster);
        setIsDirty(true);
        setSaveStatus('idle');
        setSaveError((previousError) => clearLeaseRecordsFieldError(previousError, field));

        const sectionName = keys[0];
        const trackedSection = ['master', 'lessorDetails', 'legalDescription', 'notes', 'clientAddress', 'tracts', 'invoices', 'data1099'].includes(
            sectionName
        )
            ? sectionName
            : 'master';
        const nextModifiedSections = new Set(modifiedSectionsRef.current).add(trackedSection);
        modifiedSectionsRef.current = nextModifiedSections;
        sectionVersionsRef.current[trackedSection] = (sectionVersionsRef.current[trackedSection] || 0) + 1;
        setModifiedSections(nextModifiedSections);
    };
    const handle1099NameAddressSaved = (lsId: number, values: LeaseNameAddress): void => {
        const current = editedLeaseMasterRef.current;
        if (!current) {
            return;
        }
        // Merge only the persisted fields; keep unrelated unsaved edits and section tracking.
        const merge = (lease: ILeaseMaster): ILeaseMaster => ({
            ...lease,
            data1099: lease.data1099?.map((item) => (item.lsId === lsId ? { ...item, ...values } : item)),
        });
        const next = merge(current);
        editedLeaseMasterRef.current = next;
        setEditedLeaseMaster(next);
        setLeaseMaster((baseline) => (baseline ? merge(baseline) : baseline));
    };

    const handleSectionRefresh = (section: string, value: unknown): void => {
        const currentLeaseMaster = editedLeaseMasterRef.current;
        if (!currentLeaseMaster) {
            return;
        }

        const nextEditedLeaseMaster = {
            ...currentLeaseMaster,
            [section]: value,
        } as ILeaseMaster;
        const nextModifiedSections = new Set(modifiedSectionsRef.current);
        nextModifiedSections.delete(section);

        editedLeaseMasterRef.current = nextEditedLeaseMaster;
        modifiedSectionsRef.current = nextModifiedSections;
        setEditedLeaseMaster(nextEditedLeaseMaster);
        setLeaseMaster((current) => (current ? ({ ...current, [section]: value } as ILeaseMaster) : current));
        setModifiedSections(nextModifiedSections);
        setIsDirty(nextModifiedSections.size > 0);
    };

    const refreshTractAmounts = async (): Promise<void> => {
        const currentLeaseMaster = editedLeaseMasterRef.current;
        if (!currentLeaseMaster?.lsNo) {
            throw new Error('A valid lease number is required.');
        }

        const tracts = await post(`/v1.0/leases/master/${currentLeaseMaster.lsNo}/calculate-tract-amounts`);
        if (!response.ok || !tracts) {
            throw new Error('Unable to refresh tract amounts.');
        }

        handleSectionRefresh('tracts', tracts);
    };

    const saveSections = async (requestedSections: string[]): Promise<boolean> => {
        const currentLeaseMaster = editedLeaseMasterRef.current;
        const leaseIdAtSave = leaseId;
        const sectionsToSave = requestedSections.filter((section) => modifiedSectionsRef.current.has(section));
        if (!currentLeaseMaster || sectionsToSave.length === 0) {
            return true;
        }
        if (sectionsToSave.some((section) => savingSectionsRef.current.has(section))) {
            return false;
        }

        sectionsToSave.forEach((section) => savingSectionsRef.current.add(section));
        const versionsAtSave: Record<string, number> = {};
        sectionsToSave.forEach((section) => {
            versionsAtSave[section] = sectionVersionsRef.current[section] || 0;
        });

        saveCountRef.current += 1;
        setIsSaving(true);
        setSaveStatus('saving');

        try {
            const saveData: Record<string, unknown> = {
                lsNo: currentLeaseMaster.lsNo,
                modifiedSections: sectionsToSave,
            };

            if (sectionsToSave.includes('master')) {
                saveData.master = normalizeMasterForSave({
                    kind: currentLeaseMaster.kind,
                    clNo: currentLeaseMaster.clNo,
                    prNo: currentLeaseMaster.prNo,
                    scNo: currentLeaseMaster.scNo,
                    lmNo: currentLeaseMaster.lmNo,
                    lessee: currentLeaseMaster.lessee,
                    term: currentLeaseMaster.term,
                    termUnits: currentLeaseMaster.termUnits,
                    royalty: currentLeaseMaster.royalty,
                    orri: currentLeaseMaster.orri,
                    cd: currentLeaseMaster.cd,
                    orderNo: currentLeaseMaster.orderNo,
                    clientLease: currentLeaseMaster.clientLease,
                    leaseDate: currentLeaseMaster.leaseDate,
                    bonusAcre: currentLeaseMaster.bonusAcre,
                    effDate: currentLeaseMaster.effDate,
                    draftHoldReason: currentLeaseMaster.draftHoldReason,
                    expDate: currentLeaseMaster.expDate,
                    opFl: currentLeaseMaster.opFl,
                    opTerm: currentLeaseMaster.opTerm,
                    opTermUnits: currentLeaseMaster.opTermUnits,
                    optionExercised: currentLeaseMaster.optionExercised,
                    addlOptionExercised: currentLeaseMaster.addlOptionExercised,
                    exposedDate: currentLeaseMaster.exposedDate,
                    addlOptionTerm: currentLeaseMaster.addlOptionTerm,
                    addlOptionUnits: currentLeaseMaster.addlOptionUnits,
                    loBonus: currentLeaseMaster.loBonus,
                    addlOptionAcre: currentLeaseMaster.addlOptionAcre,
                    expired: currentLeaseMaster.expired,
                    tlFl: currentLeaseMaster.tlFl,
                    rentalLease: currentLeaseMaster.rentalLease,
                    rentalInfo: currentLeaseMaster.rentalInfo,
                });
            }

            if (sectionsToSave.includes('lessorDetails')) {
                saveData.lessorDetails = currentLeaseMaster.lessorDetails;
            }
            if (sectionsToSave.includes('legalDescription')) {
                saveData.legalDescription = currentLeaseMaster.legalDescription;
            }
            if (sectionsToSave.includes('notes')) {
                saveData.notes = currentLeaseMaster.notes;
            }
            if (sectionsToSave.includes('clientAddress')) {
                saveData.clientAddress = currentLeaseMaster.clientAddress;
            }
            if (sectionsToSave.includes('invoices')) {
                saveData.invoices = currentLeaseMaster.invoices?.filter((invoice) => invoice.operation).map(normalizeInvoiceForSave);
            }
            if (sectionsToSave.includes('tracts')) {
                saveData.tracts = currentLeaseMaster.tracts?.filter((tract) => tract.operation);
            }
            if (sectionsToSave.includes('data1099')) {
                saveData.data1099 = currentLeaseMaster.data1099?.filter((item) => item.operation).map(normalize1099ForSave);
            }

            const saveResult = await put(`/v1.0/leases/master/${leaseId}`, saveData);
            if (!response.ok) {
                setSaveStatus('error');
                setSaveError(parseLeaseRecordsApiError(saveResult || response.data, response.status));
                return false;
            }
            setSaveError(null);

            // The user may have navigated while the save was in flight. Do not let the
            // old lease's refresh replace the record that is now on screen.
            if (activeLeaseIdRef.current !== leaseIdAtSave) {
                return true;
            }

            const freshLeaseMaster = await get(`/v1.0/leases/master/${currentLeaseMaster.lsNo}`);
            if (!response.ok || !freshLeaseMaster) {
                alert('Changes were saved, but the refreshed lease could not be loaded. Please reload the page.');
                return true;
            }

            if (activeLeaseIdRef.current !== leaseIdAtSave || String(freshLeaseMaster.lsNo) !== leaseIdAtSave) {
                return true;
            }

            if (!freshLeaseMaster.lessorDetails) {
                freshLeaseMaster.lessorDetails = {};
            }
            if (!freshLeaseMaster.clientAddress) {
                freshLeaseMaster.clientAddress = {};
            }

            const nextModifiedSections = new Set(modifiedSectionsRef.current);
            sectionsToSave.forEach((section) => {
                if ((sectionVersionsRef.current[section] || 0) === versionsAtSave[section]) {
                    nextModifiedSections.delete(section);
                }
            });

            const latestEditedLeaseMaster = editedLeaseMasterRef.current || currentLeaseMaster;
            const nextEditedLeaseMaster = { ...freshLeaseMaster } as ILeaseMaster;

            if (nextModifiedSections.has('master')) {
                const refreshedSections = {
                    lessorDetails: nextEditedLeaseMaster.lessorDetails,
                    legalDescription: nextEditedLeaseMaster.legalDescription,
                    notes: nextEditedLeaseMaster.notes,
                    clientAddress: nextEditedLeaseMaster.clientAddress,
                    tracts: nextEditedLeaseMaster.tracts,
                    invoices: nextEditedLeaseMaster.invoices,
                    data1099: nextEditedLeaseMaster.data1099,
                    issues: nextEditedLeaseMaster.issues,
                    transactions: nextEditedLeaseMaster.transactions,
                };
                Object.assign(nextEditedLeaseMaster, latestEditedLeaseMaster, refreshedSections);
            }

            nextModifiedSections.forEach((section) => {
                if (section !== 'master') {
                    (nextEditedLeaseMaster as unknown as Record<string, unknown>)[section] = (
                        latestEditedLeaseMaster as unknown as Record<string, unknown>
                    )[section];
                }
            });

            modifiedSectionsRef.current = nextModifiedSections;
            editedLeaseMasterRef.current = nextEditedLeaseMaster;
            setLeaseMaster(freshLeaseMaster);
            setEditedLeaseMaster(nextEditedLeaseMaster);
            setModifiedSections(nextModifiedSections);
            setIsDirty(nextModifiedSections.size > 0);
            setSaveStatus('saved');

            return true;
        } catch {
            setSaveStatus('error');
            setSaveError(parseLeaseRecordsApiError(response.data, response.status || 500));
            return false;
        } finally {
            sectionsToSave.forEach((section) => savingSectionsRef.current.delete(section));
            saveCountRef.current -= 1;
            if (saveCountRef.current === 0) {
                setIsSaving(false);
            }
        }
    };

    const handleSave = async (): Promise<boolean> => {
        return saveSections(Array.from(modifiedSectionsRef.current));
    };
    const scrollToRef = useCallback((ref: React.RefObject<HTMLDivElement>, sectionKey: string) => {
        if (ref.current) {
            setActiveSection(sectionKey);
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, []);

    const handleSectionNavigation = async (ref: React.RefObject<HTMLDivElement>, sectionKey: string): Promise<void> => {
        if (activeSection !== sectionKey && modifiedSectionsRef.current.has(activeSection)) {
            const wasSaved = await saveSections([activeSection]);
            if (!wasSaved) {
                return;
            }
        }
        scrollToRef(ref, sectionKey);
    };

    useEffect(() => {
        const container = contentRef.current;
        if (!container || !leaseMaster) {
            return () => {
                /* noop */
            };
        }

        const sectionRefs = [
            { key: 'master', ref: masterRef },
            { key: 'invoices', ref: invoiceRef },
            { key: 'lessorDetails', ref: lessorRef },
            { key: 'legalDescription', ref: descriptionRef },
            { key: 'tracts', ref: tractsRef },
            { key: 'data1099', ref: k99Ref },
            { key: 'notes', ref: notesRef },
            { key: 'clientAddress', ref: clientAddressRef },
            { key: 'transactions', ref: transactionsRef },
            { key: 'issues', ref: issuesRef },
        ];

        const handleScroll = () => {
            const containerTop = container.getBoundingClientRect().top;
            const offset = 80; // how far below the container top a section must be to count

            let currentSection = sectionRefs[0].key;

            for (const s of sectionRefs) {
                if (s.ref.current) {
                    const rect = s.ref.current.getBoundingClientRect();
                    // Section top relative to the scroll container top
                    if (rect.top - containerTop <= offset) {
                        currentSection = s.key;
                    }
                }
            }

            setActiveSection(currentSection);
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, [leaseMaster]);

    const hasActiveLease = String(leaseMaster?.lsNo) === leaseId;

    if ((isInitialLoading || leaseMaster) && !hasActiveLease && !loadError) {
        return <LeasePageLoading />;
    }

    if (loadError && !hasActiveLease) {
        return (
            <div style={{ padding: '2rem' }}>
                <p>Error loading lease.</p>
            </div>
        );
    }

    if (!leaseMaster || !editedLeaseMaster) {
        return (
            <div style={{ padding: '2rem' }}>
                <p>No lease found.</p>
            </div>
        );
    }

    const sections = [
        { label: 'Master', ref: masterRef, section: 'master' },
        { label: 'Invoices', ref: invoiceRef, section: 'invoices' },
        { label: 'Lessor', ref: lessorRef, section: 'lessorDetails' },
        { label: 'Legal Desc.', ref: descriptionRef, section: 'legalDescription' },
        { label: 'Tracts', ref: tractsRef, section: 'tracts' },
        { label: '1099', ref: k99Ref, section: 'data1099' },
        { label: 'Notes', ref: notesRef, section: 'notes' },
        { label: 'Client Addr.', ref: clientAddressRef, section: 'clientAddress' },
        { label: 'Transactions', ref: transactionsRef, section: 'transactions' },
        { label: 'Issues', ref: issuesRef, section: 'issues' },
    ];

    return (
        <div style={{ display: 'flex', justifyContent: 'center', height: '100vh', overflow: 'hidden', background: styles.colors.bgPage }}>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '1700px', overflow: 'hidden' }}>
                {/* Fixed Header Area */}
                <div style={{ flexShrink: 0, padding: '0.5rem 1.5rem 0', background: styles.colors.bgPage }}>
                    <LeaseHeaderControls
                        leaseMaster={editedLeaseMaster}
                        onNavigate={handleNavigate}
                        onProcessingNavigate={handleProcessingNavigate}
                    />
                    <div style={{ marginTop: '0.5rem' }}>
                        <LeaseHeaderSection
                            leaseMaster={editedLeaseMaster}
                            isDirty={isDirty}
                            onSave={handleSave}
                            onAutoSave={() => saveSections(Array.from(modifiedSectionsRef.current))}
                            isSaving={isSaving}
                            saveStatus={saveStatus}
                            onChange={handleFieldChange}
                        />
                    </div>
                    <LeaseRecordsErrorAlert error={saveError} onDismiss={() => setSaveError(null)} />
                </div>

                {/* Horizontal Sticky Tab Bar */}
                <div
                    style={{
                        flexShrink: 0,
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        background: styles.colors.bgPage,
                        borderBottom: 'none',
                        borderTop: 'none',
                        padding: '0.4rem 1.5rem 0.15rem',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'stretch',
                        gap: '0.25rem',
                        scrollbarWidth: 'thin',
                    }}
                >
                    {sections.map((section) => {
                        const isActive = activeSection === section.section;
                        const isModified = modifiedSections.has(section.section);
                        return (
                            <button
                                key={section.label}
                                onClick={() => void handleSectionNavigation(section.ref, section.section)}
                                style={{
                                    background: 'transparent',
                                    color: isActive ? '#1f2937' : '#4b5563',
                                    padding: '0.42rem 0.65rem 0.28rem',
                                    border: 'none',
                                    borderRadius: '6px',
                                    backgroundColor: isActive ? '#ffffff' : 'transparent',
                                    cursor: 'pointer',
                                    fontWeight: isActive ? 600 : 500,
                                    fontSize: '1.25rem',
                                    lineHeight: 1.15,
                                    transition: 'color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.375rem',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                    boxShadow: isActive ? '0 1px 1px rgba(17, 24, 39, 0.06)' : 'none',
                                }}
                                onMouseEnter={(e) => {
                                    if (!isActive) {
                                        e.currentTarget.style.color = '#111827';
                                        e.currentTarget.style.backgroundColor = '#f6f8fa';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isActive) {
                                        e.currentTarget.style.color = '#4b5563';
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }
                                }}
                            >
                                <span>{section.label}</span>
                                {isModified && (
                                    <span
                                        style={{
                                            width: '5px',
                                            height: '5px',
                                            borderRadius: '50%',
                                            backgroundColor: '#f59e0b',
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Scrollable Content */}
                <div
                    ref={contentRef}
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        backgroundColor: styles.colors.bgPage,
                        padding: '1rem 1.5rem 2rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1.25rem',
                    }}
                >
                    <div
                        ref={masterRef}
                        style={{
                            ...styles.leaseSectionFrame,
                            ...styles.leaseSectionAccents.blue,
                            ...(activeSection === 'master' ? styles.leaseSectionFrameActive : {}),
                        }}
                    >
                        <MasterSection leaseMaster={editedLeaseMaster} onChange={handleFieldChange} />
                    </div>
                    <div
                        ref={invoiceRef}
                        style={{
                            ...styles.leaseSectionFrame,
                            ...styles.leaseSectionAccents.teal,
                            ...(activeSection === 'invoices' ? styles.leaseSectionFrameActive : {}),
                        }}
                    >
                        <InvoiceSection
                            leaseMaster={editedLeaseMaster}
                            onChange={handleFieldChange}
                            onSave={() => saveSections(['invoices'])}
                            onRefresh={(invoices) => handleSectionRefresh('invoices', invoices)}
                            saveError={saveError}
                        />
                    </div>
                    <div
                        ref={lessorRef}
                        style={{
                            ...styles.leaseSectionFrame,
                            ...styles.leaseSectionAccents.amber,
                            ...(activeSection === 'lessorDetails' ? styles.leaseSectionFrameActive : {}),
                        }}
                    >
                        <LessorSection leaseMaster={editedLeaseMaster} onChange={handleFieldChange} />
                    </div>
                    <div
                        ref={descriptionRef}
                        style={{
                            ...styles.leaseSectionFrame,
                            ...styles.leaseSectionAccents.violet,
                            ...(activeSection === 'legalDescription' ? styles.leaseSectionFrameActive : {}),
                        }}
                    >
                        <DescriptionSection
                            leaseMaster={editedLeaseMaster}
                            onChange={handleFieldChange}
                            onSave={() => saveSections(['legalDescription'])}
                        />
                    </div>
                    <div
                        ref={tractsRef}
                        style={{
                            ...styles.leaseSectionFrame,
                            ...styles.leaseSectionAccents.slate,
                            ...(activeSection === 'tracts' ? styles.leaseSectionFrameActive : {}),
                        }}
                    >
                        <TractSection leaseMaster={editedLeaseMaster} onChange={handleFieldChange} onRefresh={refreshTractAmounts} />
                    </div>
                    <div
                        ref={k99Ref}
                        style={{
                            ...styles.leaseSectionFrame,
                            ...styles.leaseSectionAccents.blue,
                            ...(activeSection === 'data1099' ? styles.leaseSectionFrameActive : {}),
                        }}
                    >
                        <Section1099
                            leaseMaster={editedLeaseMaster}
                            onChange={handleFieldChange}
                            onSave={() => saveSections(['data1099'])}
                            onNameAddressSaved={handle1099NameAddressSaved}
                        />
                    </div>
                    <div
                        ref={notesRef}
                        style={{
                            ...styles.leaseSectionFrame,
                            ...styles.leaseSectionAccents.teal,
                            ...(activeSection === 'notes' ? styles.leaseSectionFrameActive : {}),
                        }}
                    >
                        <NotesSection leaseMaster={editedLeaseMaster} onChange={handleFieldChange} onSave={() => saveSections(['notes'])} />
                    </div>
                    <div
                        ref={clientAddressRef}
                        style={{
                            ...styles.leaseSectionFrame,
                            ...styles.leaseSectionAccents.amber,
                            ...(activeSection === 'clientAddress' ? styles.leaseSectionFrameActive : {}),
                        }}
                    >
                        <ClientAddressSection leaseMaster={editedLeaseMaster} onChange={handleFieldChange} />
                    </div>
                    <div
                        ref={transactionsRef}
                        style={{
                            ...styles.leaseSectionFrame,
                            ...styles.leaseSectionAccents.violet,
                            ...(activeSection === 'transactions' ? styles.leaseSectionFrameActive : {}),
                        }}
                    >
                        <TransactionsSection
                            key={editedLeaseMaster.lsNo}
                            leaseMaster={editedLeaseMaster}
                            onTransactionsRefresh={(transactions) => handleSectionRefresh('transactions', transactions)}
                        />
                    </div>
                    <div
                        ref={issuesRef}
                        style={{
                            ...styles.leaseSectionFrame,
                            ...styles.leaseSectionAccents.slate,
                            ...(activeSection === 'issues' ? styles.leaseSectionFrameActive : {}),
                        }}
                    >
                        <IssuesSection key={editedLeaseMaster.lsNo} leaseMaster={editedLeaseMaster} />
                    </div>
                    <div aria-hidden={true} style={{ height: '35vh', minHeight: '220px' }} />
                </div>
            </div>
        </div>
    );
};

export default LeasePage;



// WEBPACK FOOTER //
// ./src/components/leases/LeasePage.tsx